import { debounce } from "@mui/material";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import * as AuthService from "../app/auth";
import { AsyncActionState, AsyncActionStatus, AuthResponse, Token } from "../models";
import { getTimeDifference } from "../utils";
import { RootState } from "./store";

export interface AuthState {
  authenticate: AsyncActionState<AuthResponse>;
  token: Token | null;
  authenticated: boolean;
}

const initialState: AuthState = {
  authenticate: {
    status: AsyncActionStatus.idle,
    error: null,
    result: null,
  },
  token: null,
  authenticated: false,
};

export const authenticateAsync = createAsyncThunk("auth/authenticate", async (_: void, thunkAPI) => {
  const response = await AuthService.login();
  const parsedToken = response?.accessToken ? jwtDecode<Token>(response.accessToken) : null;
  if (parsedToken && AuthService.isTokenValid(parsedToken)) {
    const expiration = getTimeDifference(new Date(1000 * (parsedToken.exp ?? 0)), new Date(Date.now()));
    const refreshInMinutes = expiration.minutes - 10;
    console.log(`auth refresh schedules in ${refreshInMinutes} minutes`);
    // schedule a refresh
    setTimeout(() => {
      console.log("refresh access token");
      thunkAPI.dispatch(authenticateAsync());
    }, refreshInMinutes * 60000);
    return response;
  }
});
export const authenticateAsyncDebounced = debounce((dispatch) => {
  dispatch(authenticateAsync());
});

export const authSlice = createSlice({
  name: "auth",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setAuthenticated: (state, token: PayloadAction<boolean>) => {
      state.authenticated = token.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authenticateAsync.pending, (state) => {
        state.authenticate.status = AsyncActionStatus.running;
        state.authenticate.error = null;
      })
      .addCase(authenticateAsync.fulfilled, (state, action) => {
        state.authenticate.status = AsyncActionStatus.success;
        state.authenticate.result = action.payload ?? null;
        state.token = state.authenticate.result?.accessToken ? jwtDecode<Token>(state.authenticate.result?.accessToken) : null;
        state.authenticated = state.authenticate.result?.isSuccess ?? false;
      })
      .addCase(authenticateAsync.rejected, (state, action) => {
        state.authenticate.status = AsyncActionStatus.error;
        state.authenticate.error = action.error.message || "Something went wrong";
      });
  },
});

export const { setAuthenticated } = authSlice.actions;

export const selectAuthenticate = (state: RootState): AsyncActionState<AuthResponse> => state.auth.authenticate;
export const selectAuthenticated = (state: RootState): boolean => state.auth.authenticated;
export const selectToken = (state: RootState): Token | null => state.auth.token;
export const selectAuthResult = (state: RootState): AuthResponse | null => state.auth.authenticate.result;

export default authSlice.reducer;
